import type { AutocompleteProps } from '@mui/material/Autocomplete';
import type { ResourceUser } from '@src/features/absence';
import Avatar from '@src/shared/ui/Avatar';
import type {
  AutocompleteOption,
  AutocompleteRenderOptionState,
  InputProps,
} from '@src/shared/ui/inputs';
import { AutocompleteInput, InputAdornment } from '@src/shared/ui/inputs';
import { ListItem, ListItemAvatar, ListItemText } from '@src/shared/ui/List';
import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';

interface UserAutocompleteOption extends AutocompleteOption {
  avatar: string | undefined;
}

type UserAutocompleteInputProps = Omit<
  AutocompleteProps<UserAutocompleteOption, false, false, false>,
  'renderInput' | 'value' | 'options' | 'onChange'
> & {
  value?: UserAutocompleteOption['value'] | null;
  users: ResourceUser[];
  onChange: (value: UserAutocompleteOption['value'] | null) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  InputProps: InputProps;
  label?: React.ReactNode;
  placeholder: string;
};

const StyleSheet = {
  field: {
    '& .MuiInputBase-adornedStart': {
      paddingLeft: '3px',
    },
  },
  avatarAdornment: {
    marginTop: '3px',
    marginRight: 0,
    marginBottom: '3px',
  },
  avatar: {
    width: '32px',
    height: '32px',
  },
};

const UserAutocompleteInput = forwardRef<
  HTMLInputElement,
  UserAutocompleteInputProps
>((props, ref) => {
  const { users, value, ...restProps } = props;
  const renderOption = (
    props: HTMLAttributes<HTMLLIElement> & { key: string },
    option: ResourceUserOption,
    _state: AutocompleteRenderOptionState,
  ) => {
    const { key, ...rest } = props;
    return (
      <ListItem key={key} {...rest}>
        <ListItemAvatar>
          <Avatar src={option.avatar} />
        </ListItemAvatar>
        <ListItemText>{option.label}</ListItemText>
      </ListItem>
    );
  };
  const valueAvatar = () => {
    const portrait = users.find((u) => u.id === value)?.portrait;
    if (portrait) return portrait;
  };
  return (
    <AutocompleteInput
      value={value}
      ref={ref}
      {...restProps}
      options={usersToOptions(users)}
      renderOption={renderOption}
      sx={StyleSheet.field}
      InputProps={{
        startAdornment: value && (
          <InputAdornment position="start" sx={StyleSheet.avatarAdornment}>
            <Avatar src={valueAvatar()} sx={StyleSheet.avatar} />
          </InputAdornment>
        ),
        size: 'medium',
      }}
    />
  );
});

type ResourceUserOption = {
  label: string;
  value: string | number;
  avatar: string | undefined;
};

function usersToOptions(users: ResourceUser[]) {
  return users.map((user) => ({
    label: user.name,
    value: user.id,
    avatar: user.portrait || undefined,
  }));
}
export default UserAutocompleteInput;
